import { LocalDate, YearMonth } from "@js-joda/core";
import {
    createDeserializer,
    handleJsonApiError,
    injectPageParams,
    type PageParams,
} from "@jsonapi-serde/client";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod/mini";
import { zj } from "zod-joda/mini";
import { apiUrl } from "@/utils/api.ts";
import { localZoneId } from "@/utils/datetime.ts";
import { decimalSchema } from "@/utils/zod.ts";

const standardParcelSchema = z.object({
    acreage: decimalSchema(),
    startingBid: z.nullable(z.int()),
    biddingIncrement: z.nullable(z.int()),
});

export type StandardParcel = z.output<typeof standardParcelSchema>;

const choiceParcelSchema = z.object({
    acreage: decimalSchema(),
});

export type ChoiceParcel = z.output<typeof choiceParcelSchema>;

const parcelCombinationSchema = z.object({
    parcelIndices: z.array(z.int()),
    name: z.string(),
    startingBid: z.nullable(z.int()),
    biddingIncrement: z.nullable(z.int()),
});

export type ParcelCombination = z.output<typeof parcelCombinationSchema>;

export const configSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("single_parcel"),
        parcel: standardParcelSchema,
    }),
    z.object({
        type: z.literal("multi_parcel"),
        parcels: z.array(standardParcelSchema),
    }),
    z.object({
        type: z.literal("combo"),
        parcels: z.array(standardParcelSchema),
        parcelCombinations: z.array(parcelCombinationSchema),
    }),
    z.object({
        type: z.literal("choice"),
        startingBid: z.nullable(z.int()),
        biddingIncrement: z.nullable(z.int()),
        parcels: z.array(choiceParcelSchema),
    }),
]);

const priceModelSchema = z.enum(["per_acre", "total"]);
export type PriceModel = z.output<typeof priceModelSchema>;

export const auctionAttributesSchema = z.object({
    type: z.enum(["single_parcel", "multi_parcel", "combo", "choice"]),
    sealedBid: z.boolean(),
    state: z.enum(["registration_open", "registration_closed", "running", "completed"]),
    priceModel: priceModelSchema,
    title: z.string(),
    description: z.string(),
    location: z.nullable(z.string()),
    startsAt: z.pipe(
        zj.zonedDateTime(),
        z.transform((value) => value.withZoneSameInstant(localZoneId)),
    ),
    zoomMeetingUrl: z.nullable(z.url()),
    brochureUrl: z.nullable(z.url()),
    agentEmailAddress: z.nullable(z.email()),
    config: configSchema,
});

const deserializeAuction = createDeserializer({
    type: "auction",
    cardinality: "one",
    attributesSchema: auctionAttributesSchema,
});

const deserializeAuctions = createDeserializer({
    type: "auction",
    cardinality: "many",
    attributesSchema: z.pick(auctionAttributesSchema, {
        type: true,
        sealedBid: true,
        state: true,
        title: true,
        location: true,
        startsAt: true,
    }),
    metaSchema: z.object({
        numParcels: z.int().check(z.positive()),
        isStartable: z.boolean(),
    }),
});

export type Auction = ReturnType<typeof deserializeAuction>["data"];
export type PaginatedAuctions = ReturnType<typeof deserializeAuctions>;
export type ListAuction = PaginatedAuctions["data"][number];

export type ListAuctionsOptions = {
    pageParams?: PageParams;
    status?: "active" | "completed";
    startsAtRange?: YearMonth | number | null;
};

export const createAuctionQueryOptionsFactory = (authFetch: typeof fetch) => ({
    list: (options?: ListAuctionsOptions) =>
        queryOptions({
            queryKey: ["auctions", options],
            queryFn: async ({ signal }) => {
                const url = apiUrl("/auctions");
                url.searchParams.set(
                    "fields[auction]",
                    "type,sealedBid,state,title,location,startsAt",
                );
                injectPageParams(url, options?.pageParams);

                if (options?.status) {
                    url.searchParams.set("filter[status]", options.status);
                }

                if (options?.startsAtRange) {
                    url.searchParams.set(
                        "filter[startsAt][from]",
                        (options.startsAtRange instanceof YearMonth
                            ? options.startsAtRange.atDay(1)
                            : LocalDate.of(options.startsAtRange, 1, 1)
                        )
                            .atStartOfDay()
                            .atZone(localZoneId)
                            .toInstant()
                            .toString(),
                    );
                    url.searchParams.set(
                        "filter[startsAt][to]",
                        (options.startsAtRange instanceof YearMonth
                            ? options.startsAtRange.atEndOfMonth()
                            : LocalDate.of(options.startsAtRange, 12, 31)
                        )
                            .atStartOfDay()
                            .plusDays(1)
                            .minusNanos(1)
                            .atZone(localZoneId)
                            .toInstant()
                            .toString(),
                    );
                }

                const response = await authFetch(url, { signal });
                await handleJsonApiError(response);
                return deserializeAuctions(await response.json());
            },
        }),
    get: (auctionId: string) =>
        queryOptions({
            queryKey: ["auction", auctionId],
            queryFn: async ({ signal }) => {
                const response = await fetch(apiUrl(`/auctions/${auctionId}`), { signal });
                await handleJsonApiError(response);
                return deserializeAuction(await response.json()).data;
            },
        }),
});
